<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'JobModal',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    id: {
      type: String,
      required: true,
      default: null,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const item = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'xl',
  }));
  const b_5074589820235598_model = computed(() => '{{ item.created_by }');

  const $data = computed(() => ({
    item: item.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
    b_5074589820235598_model: b_5074589820235598_model.value,
  }));

  const $$load_item = () => {
    return props.id !== null
      ? $fn.value.fetch('read_a_job', { parameters: { id: props.id } })
      : null;
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'item' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
      {
        name: 'b_5074589820235598_model',
        nodeId: '5074589820235598',
        dependsOn: ['item'],
      },
    ],
    loaders: { item: $$load_item },
    ux: {},
    dataRefs: {
      item,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
      b_5074589820235598_model,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    if (loaded.value) {
      return { title: [item.value.number, $fn.value.gettext('Job')] };
    }
    return null;
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.id,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('id');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_a47t75qq5_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_2140198529698299_setData_sgihtlge3_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'item',
      value: $event.data.response.data,
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_2140198529698299_request_l4w3mzvzg_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'update_job',
      data: {
        status: 'Not Started',
      },
      parameters: {
        id: item.value.id,
      },
    };
  };

  const act_7315092382398562_closeModal_a47t75qq5 =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_a47t75qq5_getActionArgs,
    });
  const inst_evh_7315092382398562_close_hhqpkn6hz =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_a47t75qq5,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_hhqpkn6hz],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_5ub4vuvwp =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_7qxpc72qr =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_5ub4vuvwp,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_7qxpc72qr],
    event: 'reload',
  });
  const act_8564662037462133_closeComponent_tspzdbqcv =
    new actionTypes.CloseComponentAction({});
  const inst_evh_8564662037462133_close_aql8yd761 =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeComponent_tspzdbqcv,
      event: 'close',
      displayName: 'closeComponent',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_aql8yd761],
    event: 'close',
  });
  const act_2140198529698299_setData_sgihtlge3 = new actionTypes.SetDataAction({
    actionArgs: act_2140198529698299_setData_sgihtlge3_getActionArgs,
  });
  const inst_evh_2140198529698299_success_2lnlgwgxy =
    new actionTypes.EventHandlerImpl({
      action: act_2140198529698299_setData_sgihtlge3,
      event: 'success',
      displayName: 'setData',
    });
  const act_2140198529698299_request_l4w3mzvzg = new actionTypes.RequestAction({
    actionArgs: act_2140198529698299_request_l4w3mzvzg_getActionArgs,
    displayName: 'restart',
    events: [inst_evh_2140198529698299_success_2lnlgwgxy],
  });
  const inst_evh_2140198529698299_clickPreventStop_d9621ibew =
    new actionTypes.EventHandlerImpl({
      action: act_2140198529698299_request_l4w3mzvzg,
      event: 'click.prevent.stop',
      displayName: 'restart',
    });
  const inst_evh_2140198529698299_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2140198529698299_clickPreventStop_d9621ibew],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_hhqpkn6hz,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_7qxpc72qr,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_aql8yd761,
    inst_evh_8564662037462133_close,
    inst_evh_2140198529698299_success_2lnlgwgxy,
    inst_evh_2140198529698299_clickPreventStop_d9621ibew,
    restart: inst_evh_2140198529698299_clickPreventStop_d9621ibew,
    inst_evh_2140198529698299_clickPreventStop,
  };

  const evh_7315092382398562_close_hhqpkn6hz = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_hhqpkn6hz.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_7qxpc72qr = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_7qxpc72qr.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_aql8yd761 = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_aql8yd761.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2140198529698299_success_2lnlgwgxy = async (event, scope) => {
    return await actions.inst_evh_2140198529698299_success_2lnlgwgxy.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2140198529698299_clickPreventStop_d9621ibew = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2140198529698299_clickPreventStop_d9621ibew.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2140198529698299_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2140198529698299_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="JobModal"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :title="documentTitle"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <div class="row">
        <ItpFormGroup
          :cols-md="3"
          name="status"
          :label="pgettext('Job', 'Status')"
        >
          <ItpFormInput name="status" size="md" v-model="item.status" readonly>
          </ItpFormInput>
        </ItpFormGroup>
        <ItpFormGroup
          :cols-md="3"
          name="job_type"
          :label="pgettext('Job', 'Job type')"
        >
          <ItpFormInput
            name="job_type"
            size="md"
            v-model="item.job_type"
            readonly
          >
          </ItpFormInput>
        </ItpFormGroup>
        <ItpFormGroup
          :cols-md="3"
          name="created_at"
          :label="gettext('Created at')"
        >
          <ItpFormInput
            name="created_at"
            size="md"
            v-model="item.created_at"
            readonly
          >
          </ItpFormInput>
        </ItpFormGroup>
        <ItpFormGroup
          :cols-md="3"
          name="created_by"
          :label="gettext('Created by')"
        >
          <ItpFormInput
            name="created_by"
            size="md"
            :model="b_5074589820235598_model"
            readonly
          >
          </ItpFormInput>
        </ItpFormGroup>
      </div>
      <hr />
      <div>
        <label>
          {{ gettext('Job data') }}
        </label>
        <ItpJSONToTable :truncate-text="200" :json="item.job_data">
        </ItpJSONToTable>
      </div>
      <hr />
      <div>
        <label>
          {{ gettext('Job result') }}
        </label>
        <ItpJSONToTable :truncate-text="200" :json="item.job_result">
        </ItpJSONToTable>
      </div>
    </UcBody>
    <template #footer v-if="loaded">
      <DIV class="modal-footer">
        <ItpButton
          :text="gettext('Restart')"
          variant="danger"
          :spinning="actions.restart.isRunning"
          class="mr-auto"
          v-if="item.status == 'Errored'"
          @click.prevent.stop="
            evh_2140198529698299_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </DIV>
    </template>
  </ItpPage>
</template>
